import {
  audienceBuilderEdgeMap,
  audienceBuilderNodeMap,
} from '#lit/common/constants/builders/audience_builder.js';
import type {GraphologyConfig} from '#lit/lib/builder/stores/graphology.js';
import {GraphologyStore} from '#lit/lib/builder/stores/graphology.js';
import {Channel} from '#utils/channel.js';
import {AudienceBuilderTemplateKeys} from '#lit/types/builders/audience_builder/index.js';
import {AudienceBuilder} from './builder.js';
import {defaultTemplate} from './templates/default_template.js';

export const AUDIENCE_BUILDER_KEY = 'ab-default';
export const AUDIENCE_BUILDER_MAX_SIZE = 3;
export const AUDIENCE_BUILDER_NEST_LIMIT = 1;

export interface AudienceBuilderManagerConfig {
  nestLimit: number;
  maxSize: number; // Max non-default builders (default: 3)
}

/**
 * Singleton manager for audience builder instances.
 * Manages multiple audience builder instances based on keys/names,
 * providing isolation between different contexts (e.g., main builder vs chat preview).
 */
export class AudienceBuilderManager {
  private static instance: AudienceBuilderManager;
  private builders: Map<string, AudienceBuilder> = new Map();
  private config: AudienceBuilderManagerConfig;
  private defaultKey: string;

  private constructor(
    config: AudienceBuilderManagerConfig = {
      nestLimit: AUDIENCE_BUILDER_NEST_LIMIT,
      maxSize: AUDIENCE_BUILDER_MAX_SIZE,
    }
  ) {
    this.config = config;
    this.defaultKey = AUDIENCE_BUILDER_KEY;
  }

  /**
   * Get the singleton instance of the manager
   */
  static getInstance(
    config?: AudienceBuilderManagerConfig
  ): AudienceBuilderManager {
    if (!AudienceBuilderManager.instance) {
      AudienceBuilderManager.instance = new AudienceBuilderManager(config);
    }
    return AudienceBuilderManager.instance;
  }

  /**
   * Get or create an audience builder instance for the given key
   */
  getBuilder(key?: string): AudienceBuilder {
    const builderKey = key || this.defaultKey;
    const isDefaultKey = builderKey === this.defaultKey;

    if (this.hasBuilder(builderKey)) {
      const builder = this.builders.get(builderKey);
      if (!builder) {
        throw new Error(`Builder not found for key: ${builderKey}`);
      }

      // For non-default builders, implement LRU by moving to end
      if (!isDefaultKey) {
        this.builders.delete(builderKey);
        this.builders.set(builderKey, builder);
      }

      return builder;
    }

    // Creating new builder - check if we need to evict
    if (!isDefaultKey) {
      this.evictIfNeeded();
    }

    const newBuilder = this.createBuilder(builderKey);
    this.builders.set(builderKey, newBuilder);
    return newBuilder;
  }

  /**
   * Check if a builder exists for the given key
   */
  hasBuilder(key: string): boolean {
    return this.builders.has(key);
  }

  /**
   * Remove a builder instance (useful for cleanup)
   */
  removeBuilder(key: string): boolean {
    if (this.hasBuilder(key)) {
      return this.builders.delete(key);
    }
    return false;
  }

  /**
   * Get all active builder keys
   */
  getAllKeys(): string[] {
    return Array.from(this.builders.keys());
  }

  /**
   * Clear all builders (useful for testing or reset scenarios)
   */
  clearAll(): void {
    // Clear all builders - no cleanup needed since AudienceBuilder doesn't have destroy method
    this.builders.clear();
  }

  /**
   * Create a new audience builder instance
   */
  private createBuilder(key: string): AudienceBuilder {
    const channel = Channel.create(`ab:${key}`);
    const config: GraphologyConfig = {
      nodeClassMap: audienceBuilderNodeMap,
      edgeClassMap: audienceBuilderEdgeMap,
    };

    const store = GraphologyStore.create({config});

    const builder = new AudienceBuilder({
      key,
      store,
      channel,
      nestLimit: this.config.nestLimit,
    });

    // Add the channel as a sink so the builder emits to it
    builder.addSinkChannel(channel);

    // Register templates for each instance
    builder.setTemplates({
      [AudienceBuilderTemplateKeys.DEFAULT]: defaultTemplate,
    });

    return builder;
  }

  /**
   * Evicts the least recently used non-default builder if maxSize is reached.
   */
  private evictIfNeeded(): void {
    const nonDefaultCount = this.getNonDefaultBuilderCount();

    if (nonDefaultCount >= this.config.maxSize) {
      // Find first non-default builder (LRU) and remove it
      for (const [key] of this.builders) {
        if (key !== this.defaultKey) {
          this.builders.delete(key);
          break;
        }
      }
    }
  }

  /**
   * Count the number of non-default builders
   */
  private getNonDefaultBuilderCount(): number {
    return Array.from(this.builders.keys()).filter(
      key => key !== this.defaultKey
    ).length;
  }
}

// Export convenience functions for backward compatibility and ease of use
export const builderManager = AudienceBuilderManager.getInstance();

/**
 * Get the default audience builder instance
 */
export const getDefaultBuilder = (): AudienceBuilder =>
  builderManager.getBuilder();

/**
 * Get a builder instance by key
 */
export const getBuilder = (key?: string): AudienceBuilder =>
  builderManager.getBuilder(key);
